import * as React from 'react';
import { ITelemetryNamespaceLogger, OfficeFluidErrorType } from '@fluidx/office-fluid-types';
import { NamespaceLoggerAdapter } from '@fluidx/utilities';
import { ContainerBannerProvider, FluidMessageBannerProps } from '../errorUX';
import { Loading } from '../Loading';
import { ComponentUXContext } from './ComponentUXContextProvider';

const FluidMessageBanner = React.lazy(() =>
  import(/* webpackChunkName: "containerMessageBanner", webpackPrefetch: true */ '../errorUX').then((m) => ({
    default: m.FluidMessageBanner
  }))
);

/**
 * A custom FluidMessageBanner with a ContainerBannerProvider instance as a default banner source
 * This control is only usable within the context of a composed ComponentUX experience
 * @alias MessageBar : formerly named MessageBar
 */
export const ComponentUXMessageBanner = React.memo(
  ({ bannerProviders, containerBannerActionHandlers, logger, stackConfiguration }: ComponentUXMessageBannerProps) => {
    const { container, localization } = React.useContext(ComponentUXContext)!;

    return container ? (
      <React.Suspense fallback={<Loading />}>
        <FluidMessageBanner
          locale={localization.locale}
          stackConfiguration={stackConfiguration}
          bannerProviders={[
            new ContainerBannerProvider(
              container,
              localization.locale,
              containerBannerActionHandlers,
              // Since this logger comes directly from the host app, wrap it with the appropriate namespace
              logger && new NamespaceLoggerAdapter('ErrorUX', logger)
            ),
            ...bannerProviders
          ]}
        />
      </React.Suspense>
    ) : null;
  }
);

/**
 * A ComponentUX custom FluidMessageBanner with a ContainerBannerProvider instance as a default banner source
 * @alias ComponentUXMessageBanner
 */
export const MessageBar = ComponentUXMessageBanner;

export interface ComponentUXMessageBannerProps extends Omit<FluidMessageBannerProps, 'locale'> {
  /**
   * The action handler callback for different error scenarios.
   * It's implementation should fan out to handle all expected action types below.
   *
   * Reload - Reloads container and component.   *
   * Upgrade - Upgrade version of container and component. Usually this requires a Reload   *
   * ViewFileLocation - Open file storage location. User has been prompted to delete some files in ODSP out of storage situations.
   */
  containerBannerActionHandlers: <
    ActonType extends 'Reload' | 'Upgrade' | 'ViewFileLocation',
    ErrorType extends OfficeFluidErrorType
  >(
    actionType: ActonType,
    errorType: ErrorType
  ) => void;

  /** Namespaced telemetry logger */
  logger?: ITelemetryNamespaceLogger;
}
